import { uiText } from '../i18n/uiText';
import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlertTriangle, TrendingUp, Building2,
  CheckCircle2, AlertCircle, Download, ChevronRight, X,
} from 'lucide-react';
import { executiveDashboardApi, getApiErrorMessage } from '../api';
import { LoadingState } from '../components/common/UI';
import PageIntro from '../components/ui/PageIntro';

const TILES = {
  green: {
    bg: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
    border: '#86efac', accent: '#15803d', icon: CheckCircle2,
    label: uiText("Зелёные"), desc: uiText("Надёжные поставщики"),
  },
  yellow: {
    bg: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
    border: '#fcd34d', accent: '#a16207', icon: AlertCircle,
    label: uiText("Жёлтые"), desc: uiText("Требуют внимания"),
  },
  red: {
    bg: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
    border: '#fca5a5', accent: '#b91c1c', icon: AlertTriangle,
    label: uiText("Красные"), desc: uiText("Критический риск"),
  },
  gray: {
    bg: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
    border: '#d1d5db', accent: '#4b5563', icon: Building2,
    label: uiText("Без оценки"), desc: uiText("Ещё не оценены"),
  },
};

const COLOR_PILL = {
  green: { bg: '#dcfce7', color: '#166534' },
  yellow: { bg: '#fef3c7', color: '#854d0e' },
  red: { bg: '#fee2e2', color: '#991b1b' },
  gray: { bg: '#f3f4f6', color: '#4b5563' },
};

const CATEGORY_LABELS = {
  supplier: uiText("Поставщик"),
  contractor: uiText("Подрядчик"),
  ecosystem_entity: uiText("Юр. лицо экосистемы"),
  partner: uiText("Партнёр сети"),
};

export default function ExecutiveDashboardPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tiles, setTiles] = useState(null);
  const [criticalTop, setCriticalTop] = useState([]);
  const [dynamics, setDynamics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [summaryRows, setSummaryRows] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const summaryRef = useRef(null);
  const summaryView = searchParams.get('view') || '';

  useEffect(() => {
    let alive = true;
    Promise.all([
      executiveDashboardApi.tiles().catch(() => ({ data: null })),
      executiveDashboardApi.criticalTop(5).catch(() => ({ data: [] })),
      executiveDashboardApi.dynamics(90).catch(() => ({ data: null })),
    ]).then(([t, c, d]) => {
      if (!alive) return;
      setTiles(t.data);
      setCriticalTop(c.data || []);
      setDynamics(d.data);
    }).catch(e => {
      if (alive) setError(getApiErrorMessage(e));
    }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!summaryView) {
      setSummaryRows([]);
      setSummaryLoading(false);
      return undefined;
    }
    let alive = true;
    setSummaryLoading(true);
    setError('');
    const ratingColor = ['green', 'yellow', 'red', 'gray'].includes(summaryView) ? summaryView : undefined;
    executiveDashboardApi.companiesSummary(ratingColor)
      .then((response) => {
        if (!alive) return;
        const rows = Array.isArray(response.data) ? response.data : [];
        setSummaryRows(summaryView === 'attention'
          ? rows.filter((row) => row.overdue_blocking_count > 0 || row.has_active_flag)
          : rows);
        window.requestAnimationFrame(() => summaryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
      })
      .catch((e) => { if (alive) setError(getApiErrorMessage(e)); })
      .finally(() => { if (alive) setSummaryLoading(false); });
    return () => { alive = false; };
  }, [summaryView]);

  const openSummary = (view) => {
    const params = new URLSearchParams(searchParams);
    params.set('view', view);
    setSearchParams(params);
  };

  const closeSummary = () => {
    const params = new URLSearchParams(searchParams);
    params.delete('view');
    setSearchParams(params);
  };

  if (loading) {
    return <LoadingState message={uiText("Загружаем сводку по компаниям…")} />;
  }

  const totalSuppliers = tiles?.tiles?.total || 0;
  const hasAlerts = (tiles?.active_red_flags || 0) > 0 || (tiles?.overdue_blocking_issues || 0) > 0;

  return (
    <div className="page-content">
      <PageIntro
        icon={TrendingUp}
        title={uiText("Сводка по компаниям")}
        description={uiText("Рейтинги поставщиков, динамика оценок и критические риски в одном представлении.")}
        aside={(
          <button
            type="button"
            className="btn btn-outline"
            onClick={async () => {
              try {
                const res = await executiveDashboardApi.exportXlsx();
                const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `executive-summary-${new Date().toISOString().slice(0,10)}.xlsx`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
              } catch (e) {
                setError(getApiErrorMessage(e));
              }
            }}
          >
            <Download size={15}/>{uiText("Экспорт XLSX")}</button>
        )}
      />

      {error && <div className="alert alert-error" style={{ marginBottom: 14 }}>{error}</div>}

      <div style={{
        display: 'grid', gap: 16, marginBottom: 24,
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
      }}>
        {['green', 'yellow', 'red', 'gray'].map(k => {
          const t = TILES[k];
          const Icon = t.icon;
          const count = tiles?.tiles?.[k] || 0;
          const pct = totalSuppliers > 0 ? Math.round(count / totalSuppliers * 100) : 0;
          return (
            <button type="button" key={k} className="executive-summary-tile" style={{
              background: t.bg, border: `1px solid ${t.border}`,
              borderRadius: 14, padding: '20px 22px',
              transition: 'transform 0.2s, box-shadow 0.2s',
            }}
            onClick={() => openSummary(k)}
            aria-expanded={summaryView === k}
            aria-label={uiText("Показать компании: {v0}", {v0: (t.label.toLowerCase())})}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{
                    fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase',
                    color: t.accent, opacity: 0.8,
                  }}>{t.label}</div>
                  <div style={{
                    fontSize: 44, fontWeight: 800, color: t.accent, lineHeight: 1, marginTop: 6,
                  }}>{count}</div>
                  <div style={{ fontSize: 12, color: t.accent, opacity: 0.75, marginTop: 6 }}>
                    {t.desc}{totalSuppliers > 0 ? ` · ${pct}%` : ''}
                  </div>
                </div>
                <div style={{
                  background: 'rgba(255,255,255,0.5)', borderRadius: 10, padding: 8,
                }}>
                  <Icon size={20} style={{ color: t.accent }}/>
                </div>
              </div>
              {totalSuppliers > 0 && pct > 0 && (
                <div style={{
                  marginTop: 14, height: 6, background: 'rgba(255,255,255,0.4)',
                  borderRadius: 3, overflow: 'hidden',
                }}>
                  <div style={{
                    width: `${pct}%`, height: '100%', background: t.accent,
                    transition: 'width 0.4s ease',
                  }}/>
                </div>
              )}
              <span className="executive-tile-action">{uiText("Открыть список ")}<ChevronRight size={14} /></span>
            </button>
          );
        })}
      </div>

      {hasAlerts && (
        <button type="button" className="card executive-attention-card" style={{
          marginBottom: 24, border: '1px solid #fca5a5',
          background: 'linear-gradient(90deg, #fef2f2 0%, #fff 100%)',
        }} onClick={() => openSummary('attention')} aria-expanded={summaryView === 'attention'}>
          <div style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <div style={{
              width: 42, height: 42, borderRadius: 10, background: '#fee2e2',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <AlertTriangle size={20} style={{ color: '#b91c1c' }}/>
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontWeight: 700, color: '#991b1b' }}>{uiText("Требуют внимания")}</div>
              <div style={{ fontSize: 13, color: '#7f1d1d', marginTop: 2 }}>
                {tiles?.active_red_flags > 0 && (<span><b>{tiles.active_red_flags}</b>{uiText(" активных ERP-флагов")}</span>)}
                {tiles?.active_red_flags > 0 && tiles?.overdue_blocking_issues > 0 && ' · '}
                {tiles?.overdue_blocking_issues > 0 && (<span><b>{tiles.overdue_blocking_issues}</b>{uiText(" просроченных замечаний High/Medium")}</span>)}
              </div>
            </div>
            <span className="executive-attention-action">{uiText("Показать компании ")}<ChevronRight size={16} /></span>
          </div>
        </button>
      )}

      {summaryView ? (
        <section ref={summaryRef} className="card executive-company-summary" aria-live="polite">
          <div className="card-header">
            <div>
              <div className="card-title">
                {summaryView === 'attention'
                  ? uiText("Компании, требующие внимания")
                  : uiText("Компании: {v0}", {v0: (TILES[summaryView]?.label || 'сводный список')})}
              </div>
              <div className="workspace-muted">
                {summaryView === 'attention'
                  ? uiText("Только сводные показатели по просроченным замечаниям и ERP-флагам — без раскрытия карточек аудитов.")
                  : uiText("Сводный список организаций без перехода к закрытым для руководителя деталям аудитов.")}
              </div>
            </div>
            <button type="button" className="btn btn-ghost btn-sm" onClick={closeSummary} aria-label={uiText("Закрыть список компаний")} title={uiText("Закрыть")}>
              <X size={15} />{uiText(" Закрыть")}</button>
          </div>
          <div className="card-body">
            {summaryLoading ? (
              <div className="workspace-loading">{uiText("Загрузка компаний…")}</div>
            ) : summaryRows.length === 0 ? (
              <div className="workspace-empty-state">
                <div className="workspace-empty-state-title">{uiText("Компаний в этой группе нет")}</div>
                <div className="workspace-empty-state-text">{uiText("Сводка обновляется автоматически при изменении рейтингов и сроков замечаний.")}</div>
              </div>
            ) : (
              <div className="table-wrap executive-company-table-wrap">
                <table>
                  <thead><tr><th>{uiText("Компания")}</th><th>{uiText("ИНН")}</th><th>{uiText("Категории")}</th><th>{uiText("Оценка")}</th><th>{uiText("Аудитов")}</th><th>{uiText("Просрочено")}</th><th>ERP</th></tr></thead>
                  <tbody>
                    {summaryRows.map((row) => {
                      const pill = COLOR_PILL[row.rating_color] || COLOR_PILL.gray;
                      return (
                        <tr key={row.company_id}>
                          <td><strong>{row.name}</strong></td>
                          <td>{row.inn || '—'}</td>
                          <td>{(row.category_types || []).map((item) => CATEGORY_LABELS[item] || item).join(', ') || '—'}</td>
                          <td><span className="executive-rating-pill" style={{ background: pill.bg, color: pill.color }}>{row.rating_score == null ? uiText("Без оценки") : row.rating_score}</span></td>
                          <td>{row.audits_count || 0}</td>
                          <td className={row.overdue_blocking_count > 0 ? 'executive-overdue-value' : ''}>{row.overdue_blocking_count || 0}</td>
                          <td>{row.has_active_flag ? <span className="executive-erp-flag">{uiText("Активен")}</span> : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      ) : null}

      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header" style={{ borderBottom: '1px solid #f1f5f9' }}>
          <div className="card-title executive-critical-risks-title">
            <span className="executive-critical-risks-icon" aria-hidden="true"><AlertTriangle size={18}/></span>
            <span>{uiText("Топ-5 критических рисков")}</span>
          </div>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          {criticalTop.length === 0 ? (
            <div style={{ padding: '40px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>✓</div>
              <div style={{ fontWeight: 600, color: '#15803d' }}>{uiText("Поставщиков с критическим риском нет")}</div>
              <div style={{ fontSize: 13, color: 'var(--premium-text-secondary)', marginTop: 4 }}>{uiText("Это хорошие новости.")}</div>
            </div>
          ) : (
            <div>
              {criticalTop.map((r, idx) => {
                const pill = COLOR_PILL[r.rating_color] || COLOR_PILL.gray;
                return (
                  <div key={r.company_id} style={{
                    padding: '14px 20px',
                    borderBottom: idx < criticalTop.length - 1 ? '1px solid #f1f5f9' : 'none',
                    display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
                  }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: 8, fontWeight: 700,
                      background: '#f8fafc', color: '#475569',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      {idx + 1}
                    </div>
                    <div style={{ flex: 1, minWidth: 180 }}>
                      <div style={{ fontWeight: 600 }}>{r.name}</div>
                      {r.inn && <div style={{ fontSize: 11, color: 'var(--premium-text-secondary)' }}>{uiText("ИНН ")}{r.inn}</div>}
                    </div>
                    <span style={{
                      padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700,
                      background: pill.bg, color: pill.color,
                    }}>{(r.rating_color || 'gray').toUpperCase()}</span>
                    {r.rating_score != null && (
                      <div style={{ minWidth: 60, textAlign: 'center' }}>
                        <div style={{ fontSize: 11, color: 'var(--premium-text-secondary)' }}>{uiText("балл")}</div>
                        <div style={{ fontWeight: 700, color: pill.color }}>{r.rating_score}</div>
                      </div>
                    )}
                    {r.overdue_blocking_count > 0 && (
                      <div style={{ minWidth: 80, textAlign: 'center' }}>
                        <div style={{ fontSize: 11, color: 'var(--premium-text-secondary)' }}>{uiText("просрочки")}</div>
                        <div style={{ fontWeight: 700, color: '#b91c1c' }}>{r.overdue_blocking_count}</div>
                      </div>
                    )}
                    {r.has_active_flag && (
                      <span style={{
                        padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700,
                        background: '#fef2f2', color: '#b91c1c',
                      }}>{uiText("● ERP-флаг")}</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {dynamics?.points?.length > 0 ? (
        <div className="card">
          <div className="card-header" style={{ borderBottom: '1px solid #f1f5f9' }}>
            <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <TrendingUp size={18} style={{ color: '#7c3aed' }}/>{uiText("Динамика рейтингов · ")}{dynamics.days}{uiText(" дней")}</div>
          </div>
          <div className="card-body">
            <DynamicsChart points={dynamics.points.slice(-12)}/>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-body" style={{ padding: 30, textAlign: 'center', color: 'var(--premium-text-secondary)' }}>
            <TrendingUp size={32} style={{ marginBottom: 8, opacity: 0.5 }}/>
            <div>{uiText("Накопится динамика когда поставщики начнут заполнять Self-check")}</div>
          </div>
        </div>
      )}
    </div>
  );
}

function DynamicsChart({ points }) {
  const maxTotal = Math.max(...points.map(p => p.green + p.yellow + p.red), 1);
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', height: 200, marginBottom: 14 }}>
        {points.map(p => {
          const total = p.green + p.yellow + p.red;
          const heightPct = (total / maxTotal) * 100;
          return (
            <div key={p.week} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <div style={{ flex: 1, width: '100%', display: 'flex', alignItems: 'flex-end' }}>
                <div style={{
                  width: '100%', height: `${heightPct}%`, minHeight: total > 0 ? 6 : 0,
                  display: 'flex', flexDirection: 'column-reverse', borderRadius: '6px 6px 0 0',
                  overflow: 'hidden',
                }}>
                  {p.green > 0 && <div style={{ height: `${(p.green / total) * 100}%`, background: '#86efac', minHeight: 4 }} title={uiText("Зелёные: {v0}", {v0: (p.green)})}/>}
                  {p.yellow > 0 && <div style={{ height: `${(p.yellow / total) * 100}%`, background: '#fcd34d', minHeight: 4 }} title={uiText("Жёлтые: {v0}", {v0: (p.yellow)})}/>}
                  {p.red > 0 && <div style={{ height: `${(p.red / total) * 100}%`, background: '#fca5a5', minHeight: 4 }} title={uiText("Красные: {v0}", {v0: (p.red)})}/>}
                </div>
              </div>
              <div style={{ fontSize: 10, color: 'var(--premium-text-secondary)', whiteSpace: 'nowrap' }}>
                {p.week.replace(/^\d{4}-W/, 'W')}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#64748b', justifyContent: 'center' }}>
        <Legend color="#86efac" label={uiText("Зелёные")}/>
        <Legend color="#fcd34d" label={uiText("Жёлтые")}/>
        <Legend color="#fca5a5" label={uiText("Красные")}/>
      </div>
    </div>
  );
}

function Legend({ color, label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ width: 12, height: 12, background: color, borderRadius: 3 }}/>
      {label}
    </div>
  );
}
